import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Trash2, Upload, X } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../apiWithAccessToken";
import { resolveApiRoot } from "../../apiConfig";

const OUTPUT_SIZE = 256;
const CROP_BOX_SIZE = 280;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const SUPPORTED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const toAbsoluteAssetUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const apiRoot = resolveApiRoot();
  if (!apiRoot) return url;
  return `${apiRoot}${url.startsWith("/") ? "" : "/"}${url}`;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const safeRevokeObjectUrl = (url) => {
  if (typeof url === "string" && url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
};

const clampPosition = (x, y, natural, scale) => {
  const baseScale = Math.max(CROP_BOX_SIZE / natural.width, CROP_BOX_SIZE / natural.height);
  const effectiveScale = baseScale * scale;
  const renderedWidth = natural.width * effectiveScale;
  const renderedHeight = natural.height * effectiveScale;
  const maxOffsetX = Math.max((renderedWidth - CROP_BOX_SIZE) / 2, 0);
  const maxOffsetY = Math.max((renderedHeight - CROP_BOX_SIZE) / 2, 0);

  return {
    x: clamp(x, -maxOffsetX, maxOffsetX),
    y: clamp(y, -maxOffsetY, maxOffsetY),
  };
};

const ProfileImageModal = ({ isOpen, onClose, onSaved, currentUser }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [uploadWarning, setUploadWarning] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [photoActionsOpen, setPhotoActionsOpen] = useState(false);

  const dragRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });
  const imageRef = useRef(null);
  const cropAreaRef = useRef(null);
  const photoActionsRef = useRef(null);
  const previewUrlRef = useRef(null);
  const wasOpenRef = useRef(false);
  const currentName = currentUser?.name || "Guest";
  const currentAvatar = currentUser?.avatar || null;
  const getInitial = (name = "Guest") => (name.trim().charAt(0) || "G").toUpperCase();
  const hasUploadedAvatar = Boolean(currentAvatar);

  const resetEditorState = useCallback(() => {
    setSelectedFile(null);
    setNaturalSize({ width: 0, height: 0 });
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setUploadWarning("");
    setIsDragOver(false);
  }, []);

  useEffect(() => {
    previewUrlRef.current = previewUrl;
  }, [previewUrl]);

  useEffect(() => {
    if (!isOpen) {
      safeRevokeObjectUrl(previewUrlRef.current);
      setPreviewUrl(null);
      resetEditorState();
      setPhotoActionsOpen(false);
      wasOpenRef.current = false;
      return;
    }

    if (!wasOpenRef.current) {
      safeRevokeObjectUrl(previewUrlRef.current);
      setPreviewUrl(currentAvatar || null);
      resetEditorState();
      setPhotoActionsOpen(false);
      wasOpenRef.current = true;
    }
  }, [isOpen, currentAvatar, resetEditorState]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleOutsideClick = (event) => {
      if (photoActionsRef.current && !photoActionsRef.current.contains(event.target)) {
        setPhotoActionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen]);

  const baseScale = useMemo(() => {
    if (!naturalSize.width || !naturalSize.height) return 1;
    return Math.max(CROP_BOX_SIZE / naturalSize.width, CROP_BOX_SIZE / naturalSize.height);
  }, [naturalSize]);

  const renderedWidth = naturalSize.width * baseScale;
  const renderedHeight = naturalSize.height * baseScale;

  const selectFile = useCallback((file) => {
    if (!file) return;
    if (!SUPPORTED_TYPES.includes(file.type.toLowerCase())) {
      setUploadWarning("Unsupported file type. Use JPG, PNG, or WEBP.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadWarning("Image exceeds 5 MB. Please upload a smaller file.");
      return;
    }
    setUploadWarning("");
    safeRevokeObjectUrl(previewUrl);
    const objectUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setPreviewUrl(objectUrl);
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setPhotoActionsOpen(false);
  }, [previewUrl]);

  const getImageFileFromTransfer = (dataTransfer) => {
    if (!dataTransfer) return null;

    const files = Array.from(dataTransfer.files || []);
    const directImage = files.find((f) => f?.type?.startsWith("image/"));
    if (directImage) return directImage;

    const items = Array.from(dataTransfer.items || []);
    for (const item of items) {
      if (item?.kind === "file" && item?.type?.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) return file;
      }
    }
    return null;
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    const relatedTarget = event.relatedTarget;
    if (!event.currentTarget.contains(relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = getImageFileFromTransfer(event.dataTransfer);
    if (!file) {
      setUploadWarning("No image found in drop content. Use JPG, PNG, or WEBP.");
      return;
    }
    selectFile(file);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePaste = (event) => {
      const file = getImageFileFromTransfer(event.clipboardData);
      if (!file) return;
      event.preventDefault();
      selectFile(file);
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [isOpen, selectFile]);

  const handleImageLoad = (event) => {
    const img = event.currentTarget;
    setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
    setPosition({ x: 0, y: 0 });
    setScale(1);
  };

  const handleScaleChange = (event) => {
    const nextScale = Number(event.target.value);
    setScale(nextScale);
    setPosition((prev) => clampPosition(prev.x, prev.y, naturalSize, nextScale));
  };

  const onPointerDown = (event) => {
    if (!previewUrl) return;
    if (cropAreaRef.current) {
      cropAreaRef.current.setPointerCapture(event.pointerId);
    }
    dragRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
    };
  };

  const onPointerMove = (event) => {
    if (!dragRef.current.active || !previewUrl) return;
    if (dragRef.current.pointerId !== event.pointerId) return;
    const dx = event.clientX - dragRef.current.startX;
    const dy = event.clientY - dragRef.current.startY;
    const nextX = dragRef.current.originX + dx;
    const nextY = dragRef.current.originY + dy;
    setPosition(clampPosition(nextX, nextY, naturalSize, scale));
  };

  const onPointerUp = (event) => {
    if (dragRef.current.pointerId !== event.pointerId) return;
    if (cropAreaRef.current?.hasPointerCapture(event.pointerId)) {
      cropAreaRef.current.releasePointerCapture(event.pointerId);
    }
    dragRef.current = {
      active: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      originX: 0,
      originY: 0,
    };
  };

  const createCroppedBlob = async () => {
    if (!imageRef.current || !naturalSize.width || !naturalSize.height) return null;

    const effectiveScale = baseScale * scale;
    const renderedW = naturalSize.width * effectiveScale;
    const renderedH = naturalSize.height * effectiveScale;
    const left = CROP_BOX_SIZE / 2 - renderedW / 2 + position.x;
    const top = CROP_BOX_SIZE / 2 - renderedH / 2 + position.y;

    const sx = ((0 - left) / renderedW) * naturalSize.width;
    const sy = ((0 - top) / renderedH) * naturalSize.height;
    const sw = (CROP_BOX_SIZE / renderedW) * naturalSize.width;
    const sh = (CROP_BOX_SIZE / renderedH) * naturalSize.height;

    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext("2d");

    if (!ctx) return null;

    ctx.clearRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    ctx.save();
    ctx.beginPath();
    ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(imageRef.current, sx, sy, sw, sh, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    ctx.restore();

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
    });
  };

  const handleSave = async () => {
    if (!previewUrl) {
      toast.error("Please select an image first.");
      return;
    }

    setIsSaving(true);
    try {
      const blob = await createCroppedBlob();
      if (!blob) {
        toast.error("Failed to prepare cropped image.");
        return;
      }

      const formData = new FormData();
      formData.append("file", new File([blob], "profile.jpg", { type: "image/jpeg" }));

      await api.post("/auth/profile-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const meRes = await api.get("/auth/me");
      const userPayload = meRes.data?.user || meRes.data || {};
      const nextName = userPayload?.name || currentName || "Guest";
      const nextAvatar = toAbsoluteAssetUrl(
        userPayload?.profile_image || userPayload?.avatar_url || userPayload?.avatar || null
      );

      onSaved?.({ name: nextName, avatar: nextAvatar });
      toast.success("Profile photo updated.");
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to upload profile photo.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!hasUploadedAvatar) return;
    setIsRemoving(true);
    try {
      await api.delete("/auth/profile-image");
      setPreviewUrl(null);
      resetEditorState();
      setPhotoActionsOpen(false);
      onSaved?.({ name: currentName, avatar: null });
      toast.success("Profile photo removed.");
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to remove profile photo.");
    } finally {
      setIsRemoving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="profile-image-modal-overlay" onClick={onClose}>
      <div className="profile-image-modal" onClick={(e) => e.stopPropagation()}>
        <div className="profile-image-modal-header">
          <h3>Change Profile Photo</h3>
          <button
            type="button"
            className="profile-image-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div
          className={`profile-upload-dropzone ${isDragOver ? "drag-over" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
        <label className="profile-image-picker">
          <Upload size={16} />
          <span>{selectedFile ? "Choose another image" : "Choose image from device"}</span>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/jpg"
            onChange={(e) => selectFile(e.target.files?.[0])}
          />
        </label>
        </div>
        <p className="profile-upload-help">Supported: JPG, PNG, WEBP | Max size: 5 MB</p>
        {uploadWarning && <p className="profile-upload-warning">{uploadWarning}</p>}

        <div className="profile-cropper-shell">
          <div className="profile-cropper-wrap" ref={photoActionsRef}>
            <div
              ref={cropAreaRef}
              className="profile-cropper-mask"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              {previewUrl ? (
                <img
                  ref={imageRef}
                  src={previewUrl}
                  alt="Selected profile"
                  className="profile-cropper-image"
                  onLoad={handleImageLoad}
                  draggable={false}
                  style={{
                    width: `${renderedWidth}px`,
                    height: `${renderedHeight}px`,
                    transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`,
                  }}
                />
              ) : (
                <div className="profile-cropper-placeholder profile-cropper-default">
                  <div className="profile-cropper-initial">{getInitial(currentName)}</div>
                  <p>{currentName}</p>
                </div>
              )}
            </div>

            <button
              type="button"
              className="profile-edit-fab"
              onClick={() => setPhotoActionsOpen((prev) => !prev)}
              aria-label="Open profile photo actions"
            >
              <Pencil size={14} />
            </button>

            {photoActionsOpen && (
              <div className="profile-photo-actions">
                <button
                  type="button"
                  className="profile-remove-photo-btn"
                  onClick={handleRemovePhoto}
                  disabled={!hasUploadedAvatar || isRemoving || isSaving}
                  title={!hasUploadedAvatar ? "No profile photo to remove" : "Remove photo"}
                >
                  <Trash2 size={14} />
                  <span>{isRemoving ? "Removing..." : "Remove photo"}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="profile-zoom-row">
          <span>Zoom</span>
          <div className="profile-zoom-slider-wrap">
            <div className="profile-zoom-marks">
              <small>10%</small>
              <small>50%</small>
              <small>100%</small>
            </div>
            <input
              type="range"
              min="1"
              max="3"
              step="0.01"
              value={scale}
              disabled={!previewUrl}
              onChange={handleScaleChange}
            />
          </div>
        </div>

        <div className="profile-image-modal-actions">
          <button type="button" className="profile-secondary-btn" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button type="button" className="profile-primary-btn" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Photo"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileImageModal;
